import { Button } from '@components/button';
import { useCallback } from 'react';
import { useSaveChanges } from '@pages/QuoteDetails/helpers/useSaveChanges';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useStore } from '@store/GlobalStore';
import { quoteItemQueryKey } from '@services/quoteItem';
import { quoteFormQueryKeys } from '@services/quoteForm';
import { useIsMutating } from '@tanstack/react-query';
import { shallow } from 'zustand/shallow';
import { useQuoteStore } from '@store/Quote/QuoteStore';

export const HeaderSaveButton = () => {
  const [{ isLioSales }] = useStore((state) => [state.userAuth], shallow);
  const [attributesById] = useQuoteStore((state) => [state.attributesById], shallow);
  const unsavedFormAttributes = useQuoteStore((state) => state.unsavedFormAttributes);
  const formsV2SaveFn = useQuoteStore((state) => state.formsV2SaveFn);

  const hasUnsavedQuoteAttributes = Object.keys(attributesById).length > 0;
  const formsV2HasUnsaved = Object.keys(unsavedFormAttributes).length > 0;
  const unsavedChanges = hasUnsavedQuoteAttributes || formsV2HasUnsaved;

  const { quoteId } = useParams<'quoteId'>();
  invariant(quoteId);
  const isMutatingQuoteItemAttributes = useIsMutating({
    mutationKey: quoteItemQueryKey.updateQuoteItemAttributesMutationKey,
  });
  const isMutatingFormAttributes = useIsMutating({
    mutationKey: quoteFormQueryKeys.updateQuoteFormAttributesMutationKey,
  });

  const [panelInstances] = useQuoteStore((state) => [state.panelInstances], shallow);

  const { saveChanges, isLoading: saveChangesLoading } = useSaveChanges(quoteId, panelInstances);

  const handleSave = useCallback(async () => {
    // Save both quote attributes and form attributes in parallel
    const promises: Promise<void | boolean>[] = [];

    if (Object.keys(attributesById).length > 0) {
      promises.push(saveChanges());
    }

    if (formsV2HasUnsaved && formsV2SaveFn) {
      promises.push(formsV2SaveFn());
    }

    await Promise.all(promises);
  }, [attributesById, saveChanges, formsV2HasUnsaved, formsV2SaveFn]);

  const isMutating = isMutatingQuoteItemAttributes > 0 || isMutatingFormAttributes > 0;

  return isLioSales ? null : (
    <Button
      variant='outlined'
      disabled={!unsavedChanges || isMutating}
      isLoading={saveChangesLoading || isMutating}
      onClick={handleSave}
    >
      Save
    </Button>
  );
};
